import { useSyncExternalStore } from 'react'

/**
 * The single source of truth for the WIDTH axis (A6 §4.1).
 * phone < 768 · tablet 768–1119 · desktop >= 1120.
 * This hook is the ONE sanctioned place a raw media query may live; the
 * "no ad-hoc useMediaQuery" rule points here. useIsMobile() is derived from it.
 */
export type Tier = 'phone' | 'tablet' | 'desktop'

export const TIER_TABLET_MIN = 768
export const TIER_DESKTOP_MIN = 1120

function getSnapshot(): Tier {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'desktop'
  if (window.matchMedia(`(min-width: ${TIER_DESKTOP_MIN}px)`).matches) return 'desktop'
  if (window.matchMedia(`(min-width: ${TIER_TABLET_MIN}px)`).matches) return 'tablet'
  return 'phone'
}

// No window (SSR/first paint without matchMedia): match the prior useIsMobile default (not mobile).
function getServerSnapshot(): Tier {
  return 'desktop'
}

function subscribe(onChange: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}
  const queries = [
    window.matchMedia(`(min-width: ${TIER_TABLET_MIN}px)`),
    window.matchMedia(`(min-width: ${TIER_DESKTOP_MIN}px)`),
  ]
  queries.forEach((q) => q.addEventListener('change', onChange))
  return () => queries.forEach((q) => q.removeEventListener('change', onChange))
}

export function useTier(): Tier {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
